/**
 * Contracts for the Google Analytics 4 connector (owner: ads; engine at /v1/connectors, see docs/status/ga4.md).
 *
 * A customer workspace connects GA4 with the shared Google OAuth client (scope analytics.readonly only), picks one or
 * more properties from the Analytics Admin API (accountSummaries.list), and the daily sync reads the Analytics Data API
 * (runReport) into ENGAGEMENT_DB: totals by date, plus by default channel group and by source / medium.
 * A partner agency reads the same figures for a client that granted it `read:reports`, and connects GA4 for a client
 * that granted it `manage:campaigns` (see the agency connect section below).
 *
 * Behind the flag `connectors.ga4` (registered OFF, evaluated for the workspace that owns the data).
 * Kept apart from ./connectors.ts so the shared provider list only gains the `ga4` id.
 */
import { z } from 'zod';

/** Feature flag: connect, read and sync GA4. Default off (core migration 0986). */
export const GA4_FLAG = 'connectors.ga4';
export const GA4_PROVIDER = 'ga4' as const;
export const GA4_SCOPE = 'https://www.googleapis.com/auth/analytics.readonly';

/** Daily sync: a property is read again once its last sync is older than this. */
export const GA4_SYNC_INTERVAL_H = 23;
/** GA4 keeps processing recent days; re-read this many days on every sync. */
export const GA4_LOOKBACK_DAYS = 3;
/** Longest window one report request may cover (inclusive days). */
export const GA4_MAX_RANGE_DAYS = 400;
/** At most this many rows are kept per breakdown and day (the rest is folded into "(other)"). */
export const GA4_BREAKDOWN_ROW_LIMIT = 50;

/**
 * Property quota (Data API core tokens, standard properties: 200k per day, 40k per hour, 14k per project per property
 * per hour). The sync stops before a request when what is left falls under these floors and resumes after the window.
 */
export const GA4_QUOTA_FLOOR = { perHour: 2_000, perDay: 10_000, perProjectPerHour: 1_000 } as const;
/** Backoff after a 429: base x 2^(strikes-1) minutes, capped; a Retry-After header wins when longer. */
export const GA4_BACKOFF_BASE_MIN = 5;
export const GA4_BACKOFF_MAX_MIN = 24 * 60;

export const GA4_JOB_TYPES = { sync: 'connectors.ga4.sync' } as const;

export const GA4_BREAKDOWNS = ['channel', 'source_medium'] as const;
export type Ga4Breakdown = (typeof GA4_BREAKDOWNS)[number];

const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const Ga4ReportQuerySchema = z.object({ from: IsoDate, to: IsoDate });
export type Ga4ReportQuery = z.infer<typeof Ga4ReportQuerySchema>;

/**
 * Additive metrics for a day or a range. `users` is GA4 totalUsers for ONE day: users are not additive across days,
 * so range totals carry `avgDailyUsers` instead of a sum that would count returning people twice.
 */
export const Ga4MetricsSchema = z.object({
  sessions: z.number(),
  newUsers: z.number(),
  engagedSessions: z.number(),
  /** engaged / sessions; null without sessions */
  engagementRate: z.number().nullable(),
  /** GA4 key events (formerly "conversions") */
  keyEvents: z.number(),
  /** totalRevenue in minor units of the property currency; null when the property reports no currency */
  revenueMinor: z.number().nullable(),
});
export type Ga4Metrics = z.infer<typeof Ga4MetricsSchema>;

export const Ga4PropertyViewSchema = z.object({
  id: z.string(),
  connectionId: z.string(),
  propertyRef: z.string(),
  displayName: z.string().nullable(),
  currency: z.string().nullable(),
  timeZone: z.string().nullable(),
  connectionStatus: z.string(),
  /** reconnect | wait | none, as on the connection view */
  action: z.enum(['none', 'reconnect', 'wait']),
  lastSyncedAt: z.string().nullable(),
  /** set while the property quota or a 429 backoff holds the sync */
  pausedUntil: z.string().nullable(),
  pauseReason: z.enum(['quota_hour', 'quota_day', 'quota_project_hour', 'rate_limited']).nullable(),
});
export type Ga4PropertyView = z.infer<typeof Ga4PropertyViewSchema>;

export const Ga4ReportSchema = z.object({
  property: Ga4PropertyViewSchema,
  range: z.object({ from: IsoDate, to: IsoDate }),
  /** false when the connection is not connected: figures are "as of" the last success, never current */
  current: z.boolean(),
  asOf: z.string().nullable(),
  /** days in the range with no synced row (not yet synced, or before the property existed) */
  missingDates: z.array(z.string()),
  totals: Ga4MetricsSchema.extend({ avgDailyUsers: z.number().nullable() }),
  daily: z.array(Ga4MetricsSchema.extend({ date: IsoDate, users: z.number() })),
  byChannel: z.array(Ga4MetricsSchema.extend({ value: z.string() })),
  bySourceMedium: z.array(Ga4MetricsSchema.extend({ value: z.string() })),
});
export type Ga4Report = z.infer<typeof Ga4ReportSchema>;

// ------------------------------------------------------------------ agency connect for a granted client

/**
 * A partner agency connects GA4 for a client workspace it manages (partners host, partner_owner / partner_admin, an
 * ACTIVE partner_client_grants row from that client carrying this scope). The connection belongs to the client: same
 * ads_connections + ga4_properties rows as a customer connect, credential owned by the client tenant.
 */
export const GA4_AGENCY_CONNECT_SCOPE = 'manage:campaigns' as const;
/** OAuth state id prefix for agency GA4 connects: the partners host return screen routes `g4a_` states to GA4. */
export const GA4_AGENCY_STATE_PREFIX = 'g4a_';

const AgencyId = z.string().min(1).max(64).regex(/^[A-Za-z0-9_-]+$/);
const AgencyRef = z.string().min(1).max(200).regex(/^[A-Za-z0-9:._/@+-]+$/);

export const Ga4AgencyStartSchema = z.object({ reconnectConnectionId: AgencyId.optional() });
export type Ga4AgencyStart = z.infer<typeof Ga4AgencyStartSchema>;
export const Ga4AgencyCallbackSchema = z.object({ state: AgencyId, code: z.string().min(1).max(2048) });
export const Ga4AgencySelectSchema = z.object({ pendingId: AgencyId, accountRefs: z.array(AgencyRef).min(1).max(25) });

/** A client's GA4 connection as the agency sees it (no vault reference). */
export const Ga4AgencyConnectionViewSchema = z.object({
  id: z.string(),
  accountRef: z.string(),
  displayName: z.string().nullable(),
  status: z.string(),
  action: z.enum(['none', 'reconnect', 'wait']),
  lastSuccessAt: z.string().nullable(),
  createdAt: z.string(),
});
export type Ga4AgencyConnectionView = z.infer<typeof Ga4AgencyConnectionViewSchema>;

/** Callback answer: the property picker (pendingId + properties), or a reconnect done in place. */
export interface Ga4AgencyCallbackResult {
  tenantId: string;
  pendingId: string | null;
  properties: { ref: string; name: string | null }[];
  expiresAt: string | null;
  reconnected: boolean;
}
